/** Shared sub-components used by CommandBarThread (desktop) and CommandBarModal (mobile). */
import { useMemo, useState, useRef, useLayoutEffect, useId } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Zap, Check } from 'lucide-react';
import { renderChatMarkdown } from '@/shared/components/ChatMessageText';
import type { ActiveToolCall } from './useCommandBarChat';

// ─── Utilities ───────────────────────────────────────────────────────────────

export function stripModePrefix(content: string): string {
  return content.replace(/^\[Mode:\s*\w+\]\s*/i, '');
}

// ─── LogoAvatar ───────────────────────────────────────────────────────────────

export function LogoAvatar() {
  const id = useId();
  const gradA = `${id}-a`;
  const gradB = `${id}-b`;
  const gradC = `${id}-c`;

  return (
    <div
      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
      style={{ background: 'var(--color-accent-subtle)' }}
    >
      <svg width={14} height={14} viewBox="0 0 32 32" fill="none">
        <path
          d="M30 7.74V24.26C30 27.96 27.03 30.9 23.29 30.9H12.07C12.07 27.96 14.47 25.59 17.44 25.59H23.29C24.05 25.59 24.63 25.02 24.63 24.26V13.06C24.63 10.11 27.03 7.74 30 7.74Z"
          fill={`url(#${gradA})`}
        />
        <path
          d="M20.12 13.34C20.12 16.28 17.73 18.66 14.75 18.66H13.12C10.05 18.66 7.47 21.13 7.37 24.16V25.68C7.37 28.63 4.97 31 2 31V24.54C2 18.37 7.08 13.34 13.32 13.34H20.12Z"
          fill={`url(#${gradB})`}
        />
        <path
          d="M25.97 1C25.97 3.94 23.58 6.32 20.6 6.32H8.81C8.23 6.32 7.37 7.17 7.37 7.74V9.54C7.37 12.49 4.97 14.86 2 14.86V7.74C2 4.04 5.07 1 8.81 1H25.97Z"
          fill={`url(#${gradC})`}
        />
        <defs>
          <linearGradient id={gradA} x1="12" y1="8" x2="34" y2="25" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FF9B4A" />
            <stop offset="0.55" stopColor="#FF6EB1" />
            <stop offset="1" stopColor="#8A6CF0" />
          </linearGradient>
          <linearGradient id={gradB} x1="2" y1="13" x2="20" y2="31" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FF9B4A" />
            <stop offset="0.55" stopColor="#FF6EB1" />
            <stop offset="1" stopColor="#8A6CF0" />
          </linearGradient>
          <linearGradient id={gradC} x1="2" y1="1" x2="14" y2="22" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FF9B4A" />
            <stop offset="0.55" stopColor="#FF6EB1" />
            <stop offset="1" stopColor="#8A6CF0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// ─── StreamingText ─────────────────────────────────────────────────────────────

/**
 * While tokens are still arriving, withhold any trailing characters that belong
 * to an unclosed markdown marker (**bold, *italic, `code`, [link, ```fence).
 * Once the closer arrives, the whole span paints fully-formatted — users never
 * see raw asterisks/backticks flash before snapping into style.
 */
function findSafeStreamingPrefix(text: string): string {
  let safe = text;

  const fences = [...safe.matchAll(/```/g)];
  if (fences.length % 2 === 1) {
    safe = safe.slice(0, fences[fences.length - 1].index);
  }

  const bolds = [...safe.matchAll(/\*\*/g)];
  if (bolds.length % 2 === 1) {
    safe = safe.slice(0, bolds[bolds.length - 1].index);
  }

  const italics: number[] = [];
  for (let i = 0; i < safe.length; i++) {
    if (safe[i] === '*' && safe[i - 1] !== '*' && safe[i + 1] !== '*') {
      italics.push(i);
    }
  }
  if (italics.length % 2 === 1) {
    safe = safe.slice(0, italics[italics.length - 1]);
  }

  const ticks: number[] = [];
  for (let i = 0; i < safe.length; i++) {
    if (safe[i] === '`' && safe[i - 1] !== '`' && safe[i + 1] !== '`') {
      ticks.push(i);
    }
  }
  if (ticks.length % 2 === 1) {
    safe = safe.slice(0, ticks[ticks.length - 1]);
  }

  const lastOpenBracket = safe.lastIndexOf('[');
  if (lastOpenBracket !== -1 && !/\]\([^)]*\)/.test(safe.slice(lastOpenBracket))) {
    safe = safe.slice(0, lastOpenBracket);
  }

  return safe;
}

export function StreamingText({ text, isComplete }: { text: string; isComplete: boolean }) {
  const safe = useMemo(
    () => (isComplete ? text : findSafeStreamingPrefix(text)),
    [text, isComplete]
  );
  const html = useMemo(() => renderChatMarkdown(safe), [safe]);

  if (!text && !isComplete) {
    return (
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="inline-flex items-center gap-1 py-1.5"
        aria-label="thinking"
        role="status"
      >
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: 'var(--color-accent)' }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </motion.span>
    );
  }

  if (!safe) {
    return <span className="f-chat-cursor-solo" aria-hidden />;
  }

  return (
    <div
      className={`f-chat-md f-prompt-prose${isComplete ? '' : ' f-chat-streaming'}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ─── ToolStatusList ───────────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  check_subject: 'Checking your progress\u2026',
  find_weak_strong: 'Finding your weakest and strongest areas\u2026',
  check_activity: 'Checking your recent activity\u2026',
  compare_subjects: 'Comparing topics\u2026',
  query_content: 'Looking through your library\u2026',
  build_revision_session: 'Building your revision session\u2026',
  create_lesson: 'Creating your lesson\u2026',
  save_memory: 'Remembering that\u2026',
  delete_memory: 'Forgetting that\u2026',
};

export function ToolStatusList({ calls }: { calls: ActiveToolCall[] }) {
  return (
    <div className="flex flex-col gap-1.5 py-1">
      <AnimatePresence initial={false}>
        {calls.map((call) => (
          <motion.div
            key={call.id}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="flex items-center gap-2"
          >
            <motion.span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: 'var(--color-accent)' }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {TOOL_LABELS[call.name] ?? 'Working\u2026'}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── AssistantStreamingBody ──────────────────────────────────────────────────

/**
 * Renders either the active tool-call status list or the streaming reply,
 * crossfading between them. Tool status takes precedence while the reply
 * buffer is still empty — as soon as the first token arrives, the status
 * list fades out and the streamed text fades in.
 */
export function AssistantStreamingBody({
  text,
  activeToolCalls,
}: {
  text: string;
  activeToolCalls: ActiveToolCall[];
}) {
  const showTools = text === '' && activeToolCalls.length > 0;
  return (
    <AnimatePresence mode="wait" initial={false}>
      {showTools ? (
        <motion.div
          key="tools"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
        >
          <ToolStatusList calls={activeToolCalls} />
        </motion.div>
      ) : (
        <motion.div
          key="stream"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
        >
          <StreamingText text={text} isComplete={false} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── SessionActionCard ────────────────────────────────────────────────────────

export function SessionActionCard({ onStart }: { onStart: () => void }) {
  const [launching, setLaunching] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, delay: 0.15, ease: 'easeOut' }}
      className="mb-2 rounded-xl p-3"
      style={{
        background: 'var(--color-accent-subtle)',
        border: '1px solid var(--color-accent)',
        borderColor: 'color-mix(in srgb, var(--color-accent) 30%, transparent)',
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Zap size={14} style={{ color: 'var(--color-accent)' }} className="shrink-0" />
          <p
            className="truncate font-medium"
            style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
          >
            {launching ? 'Launching session...' : 'Revision session ready'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setLaunching(true);
            onStart();
          }}
          disabled={launching}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-1.5 font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-on-accent)',
            background: 'var(--color-accent)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {launching ? (
            <span
              className="h-3 w-3 rounded-full border-2"
              style={{
                borderColor: 'rgba(255,255,255,0.3)',
                borderTopColor: 'white',
                animation: 'f-spin 0.6s linear infinite',
              }}
            />
          ) : (
            <Zap size={12} />
          )}
          {launching ? 'Loading...' : 'Start Session'}
        </button>
      </div>
    </motion.div>
  );
}

// ─── JobStartedCard ──────────────────────────────────────────────────────────

export function JobStartedCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, delay: 0.15, ease: 'easeOut' }}
      className="mb-2 flex items-center gap-2 rounded-xl p-3"
      style={{
        background: 'var(--color-accent-subtle)',
        border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
      }}
    >
      <Check size={14} strokeWidth={2} style={{ color: 'var(--color-success-text)' }} />
      <p className="font-medium" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
        Generation started
      </p>
    </motion.div>
  );
}

// ─── SuggestionPills ──────────────────────────────────────────────────────────

export function SuggestionPills({
  items,
  onSelect,
}: {
  items: string[];
  onSelect: (text: string) => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  // Measure which pills fit in the available width
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const measure = measureRef.current;
    if (!wrapper || !measure) return;

    const fit = () => {
      const available = wrapper.offsetWidth;
      const children = Array.from(measure.children) as HTMLElement[];
      if (children.length === 0) return;

      const gap = 8; // gap-2 = 0.5rem = 8px
      let usedWidth = 0;
      let count = 0;

      for (const child of children) {
        const childWidth = child.offsetWidth;
        const totalNeeded = count === 0 ? childWidth : usedWidth + gap + childWidth;
        if (totalNeeded > available) break;
        usedWidth = totalNeeded;
        count++;
      }

      setVisibleCount(Math.max(count, 1));
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <motion.div
      ref={wrapperRef}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="mb-2"
    >
      {/* Hidden measurement row — matches pill styling to get accurate widths */}
      <div
        ref={measureRef}
        className="flex gap-2"
        style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
        aria-hidden
      >
        {items.map((text) => (
          <span
            key={text}
            className="shrink-0 rounded-full px-3 py-1.5 font-medium"
            style={{ fontSize: 'var(--text-xs)', border: '1px solid transparent' }}
          >
            {text}
          </span>
        ))}
      </div>
      {/* Visible pills */}
      <div className="flex gap-2">
        {items.slice(0, visibleCount).map((text) => (
          <button
            key={text}
            type="button"
            onClick={() => onSelect(text)}
            className="shrink-0 rounded-full px-3 py-1.5 font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-border)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border-strong)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
            }}
          >
            {text}
          </button>
        ))}
      </div>
    </motion.div>
  );
}
